import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Filter,
  Eye,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  Download
} from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';

const statusConfig = {
  pending: { label: 'Pendiente', color: 'bg-yellow-500/10 text-yellow-400', icon: Clock },
  confirmed: { label: 'Confirmado', color: 'bg-blue-500/10 text-blue-400', icon: CheckCircle },
  processing: { label: 'Procesando', color: 'bg-purple-500/10 text-purple-400', icon: Package },
  shipped: { label: 'Enviado', color: 'bg-indigo-500/10 text-indigo-400', icon: Truck },
  delivered: { label: 'Entregado', color: 'bg-green-500/10 text-green-400', icon: CheckCircle },
  cancelled: { label: 'Cancelado', color: 'bg-red-500/10 text-red-400', icon: XCircle }
};

const paymentStatusConfig = {
  pending: { label: 'Pendiente', color: 'text-yellow-400' },
  paid: { label: 'Pagado', color: 'text-green-400' },
  failed: { label: 'Fallido', color: 'text-red-400' },
  refunded: { label: 'Reembolsado', color: 'text-gray-400' }
};

const AdminOrders = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [statusCounts, setStatusCounts] = useState({});
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: '', payment_status: '' });
  const [selectedOrder, setSelectedOrder] = useState(null);

  const loadOrders = useCallback(async (searchTerm = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: pagination.page, limit: 20 });
      if (searchTerm) params.append('search', searchTerm);
      if (filters.status) params.append('status', filters.status);
      if (filters.payment_status) params.append('payment_status', filters.payment_status);

      const res = await fetchWithAuth(`/api/admin/orders?${params}`);
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders);
        setStatusCounts(data.status_counts);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, pagination.page, filters]);

  useEffect(() => {
    loadOrders(search);
  }, [loadOrders, search]);

  const updateOrderStatus = useCallback(async (orderId, status) => {
    try {
      const res = await fetchWithAuth(`/api/admin/orders/${orderId}`, {
        method: 'PUT',
        body: { status }
      });
      if (res.ok) {
        toast.success('Estado actualizado');
        loadOrders(search);
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  }, [fetchWithAuth, loadOrders, search]);

  const exportOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      
      const res = await fetchWithAuth(`/api/admin/orders/export/csv?${params}`);
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([data.csv_data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        a.click();
        toast.success('Exportación completada');
      }
    } catch (error) {
      toast.error('Error en exportación');
    }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(v);
  const formatDate = (d) => new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-6" data-testid="admin-orders">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-white">Pedidos</h2>
          <p className="text-gray-400 text-sm">{pagination.total} pedidos en total</p>
        </div>
        <button onClick={exportOrders} className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg">
          <Download size={18} /> Exportar CSV
        </button>
      </div>

      {/* Status Tabs */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilters(prev => ({ ...prev, status: '' }))}
          className={`px-4 py-2 rounded-lg text-sm transition-colors ${!filters.status ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        >
          Todos ({pagination.total})
        </button>
        {Object.entries(statusConfig).map(([key, config]) => (
          <button
            key={key}
            onClick={() => setFilters(prev => ({ ...prev, status: key }))}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${filters.status === key ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
          >
            {config.label} ({statusCounts[key] || 0})
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="bg-gray-800 rounded-xl p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadOrders(search)}
            placeholder="Buscar por número de orden, email..."
            className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
          />
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Orden</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Cliente</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Total</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Estado</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Pago</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Fecha</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-300">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr><td colSpan="7" className="px-4 py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div></td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-12 text-center text-gray-400"><Package className="mx-auto mb-3" size={40} /><p>No hay pedidos</p></td></tr>
              ) : orders.map(order => {
                const status = statusConfig[order.status] || statusConfig.pending;
                const payment = paymentStatusConfig[order.payment_status] || paymentStatusConfig.pending;
                return (
                  <tr key={order.id} className="hover:bg-gray-700/30">
                    <td className="px-4 py-3">
                      <p className="text-sm text-white font-mono">{order.order_number}</p>
                      <p className="text-xs text-gray-400">{order.items?.length || 0} productos</p>
                      {order.gift_note && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium bg-amber-500/20 text-amber-400 rounded" data-testid={`order-gift-badge-${order.order_number}`}>
                          🎁 Incluye regalo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-white">{order.shipping_address?.recipient_name}</p>
                      <p className="text-xs text-gray-400">{order.shipping_address?.city}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-white font-medium">{formatCurrency(order.total)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={order.status}
                        onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                        className={`text-xs px-2 py-1 rounded-full border-none ${status.color} bg-opacity-20 cursor-pointer`}
                      >
                        {Object.entries(statusConfig).map(([key, config]) => (
                          <option key={key} value={key} className="bg-gray-800 text-white">{config.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${payment.color}`}>{payment.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-400">{formatDate(order.created_at)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setSelectedOrder(order)} className="p-2 text-gray-400 hover:text-amber-500">
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
            <p className="text-sm text-gray-400">Página {pagination.page} de {pagination.pages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={pagination.page === 1} className="px-3 py-1.5 bg-gray-700 text-white rounded-lg disabled:opacity-50">Anterior</button>
              <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={pagination.page === pagination.pages} className="px-3 py-1.5 bg-gray-700 text-white rounded-lg disabled:opacity-50">Siguiente</button>
            </div>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setSelectedOrder(null)}>
          <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-white">{selectedOrder.order_number}</h3>
                  <p className="text-sm text-gray-400">{formatDate(selectedOrder.created_at)}</p>
                </div>
                <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-white">✕</button>
              </div>
              
              <div className="space-y-6">
                {/* Regalo de promoción: para armar el paquete */}
                {(selectedOrder.gift_note || (selectedOrder.promo_gifts || []).length > 0) && (
                  <div className="p-4 rounded-lg border border-amber-500/50 bg-amber-500/10" data-testid="order-gift-box">
                    <h4 className="text-sm font-semibold text-amber-400 mb-2">🎁 Incluir en el paquete (promoción)</h4>
                    {(selectedOrder.promo_gifts || []).map((g, i) => (
                      <p key={i} className="text-sm text-white">
                        {g.quantity}× perfume de {g.size_ml}ml: <strong>{g.product_code} {g.product_name}</strong>
                      </p>
                    ))}
                    {selectedOrder.gift_note && <p className="text-xs text-amber-200/80 mt-2">{selectedOrder.gift_note}</p>}
                  </div>
                )}

                {/* Notas del cliente / internas */}
                {(selectedOrder.customer_notes || selectedOrder.admin_notes) && (
                  <div className="p-3 bg-gray-700/50 rounded-lg text-sm space-y-1" data-testid="order-notes">
                    {selectedOrder.customer_notes && <p className="text-gray-300"><span className="text-gray-400">Notas del cliente:</span> {selectedOrder.customer_notes}</p>}
                    {selectedOrder.admin_notes && <p className="text-gray-300 whitespace-pre-line"><span className="text-gray-400">Notas internas:</span> {selectedOrder.admin_notes}</p>}
                  </div>
                )}

                {/* Products */}
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-3">Productos</h4>
                  <div className="space-y-2">
                    {selectedOrder.items?.map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg">
                        <img src={item.product_image} alt="" className="w-12 h-12 object-cover rounded" />
                        <div className="flex-1">
                          <p className="text-sm text-white">{item.product_code ? `${item.product_code} ` : ''}{item.product_name}</p>
                          <p className="text-xs text-gray-400">{(item.size_ml || 100) === 40 ? 35 : (item.size_ml || 100)}ml • Cant: {item.quantity} × {formatCurrency(item.unit_price)}</p>
                        </div>
                        <p className="text-sm text-amber-500">{formatCurrency(item.subtotal)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Shipping */}
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-3">Envío</h4>
                  <div className="p-3 bg-gray-700/50 rounded-lg text-sm">
                    <p className="text-white">{selectedOrder.shipping_address?.recipient_name}</p>
                    <p className="text-gray-400">{selectedOrder.shipping_address?.street}</p>
                    <p className="text-gray-400">{selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.postal_code}</p>
                    <p className="text-gray-400 mt-2">Tel: {selectedOrder.shipping_address?.phone}</p>
                  </div>
                </div>

                {/* Totals */}
                <div className="border-t border-gray-700 pt-4 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-gray-400">Subtotal</span><span className="text-white">{formatCurrency(selectedOrder.subtotal)}</span></div>
                  {selectedOrder.promo_discount > 0 && <div className="flex justify-between text-sm"><span className="text-gray-400">Promoción</span><span className="text-amber-400">-{formatCurrency(selectedOrder.promo_discount)}</span></div>}
                  {selectedOrder.discount_amount > 0 && <div className="flex justify-between text-sm"><span className="text-gray-400">Descuento</span><span className="text-green-400">-{formatCurrency(selectedOrder.discount_amount)}</span></div>}
                  <div className="flex justify-between text-sm"><span className="text-gray-400">Envío</span><span className="text-white">{formatCurrency(selectedOrder.shipping_cost)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-400">IVA</span><span className="text-white">{formatCurrency(selectedOrder.tax_amount)}</span></div>
                  <div className="flex justify-between text-lg font-semibold pt-2 border-t border-gray-700"><span className="text-white">Total</span><span className="text-amber-500">{formatCurrency(selectedOrder.total)}</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
